import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { PORTALIO_REVOKE_CONNECTION } from 'src/graphql/entities/integrations/portalio/portalio.operations';
import { useNotifications } from 'src/utils/hooks';

type PortalioRevokeConnectionQuery = {
  portalioRevokeAuthentication: {
    userId: string;
  };
};

type PortalioAuthenticateQueryVariables = {
  params: {
    userId: string;
  };
};

export const useRevokePortalioConnection = () => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const [revokeConnectionMutation] = useMutation<
    PortalioRevokeConnectionQuery,
    PortalioAuthenticateQueryVariables
  >(PORTALIO_REVOKE_CONNECTION, {
    optimisticResponse: (vars) => {
      return {
        portalioRevokeAuthentication: {
          userId: vars.params.userId,
        },
      };
    },

    onError: (error) => {
      showErrorAlert(error.message);
    },
    onCompleted: () => {
      showSuccessAlert('Revoked access to Portal.io');
    },
  });

  const revokeConnection = useCallback(
    async (portalioUserId: string, authDetailsId: string) => {
      await revokeConnectionMutation({
        variables: {
          params: {
            userId: portalioUserId,
          },
        },
        update: (cache, result) => {
          if (!result.data?.portalioRevokeAuthentication.userId) {
            return;
          }
          cache.evict({
            id: cache.identify({
              __typename: 'PortalioAuthDetails',
              id: authDetailsId,
            }),
          });
        },
      });
    },
    [revokeConnectionMutation],
  );

  return {
    revokeConnection,
  };
};
